import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

export type CharacterRow = {
  id: string;
  code: string;
  name_ar: string;
  name_en: string;
  bio_ar: string | null;
  bio_en: string | null;
  avatar_url: string | null;
  color: string;
  traits: string[];
};

export type CharacterContent = {
  id: string;
  kind: string;
  tier: string;
  age_group: string;
  title_ar: string;
  title_en: string;
  description_ar: string | null;
  description_en: string | null;
  cover_url: string | null;
  world_id: string | null;
  slug: string | null;
  published_at: string | null;
};

export const listPublicCharacters = createServerFn({ method: "GET" }).handler(
  async (): Promise<CharacterRow[]> => {
    const { data, error } = await supabaseAdmin
      .from("characters")
      .select("id,code,name_ar,name_en,bio_ar,bio_en,avatar_url,color,traits")
      .eq("is_active", true)
      .order("sort_order");
    if (error) {
      console.error("listPublicCharacters", error);
      return [];
    }
    return data ?? [];
  },
);

export const getPublicCharacterByCode = createServerFn({ method: "GET" })
  .inputValidator((input) =>
    z
      .object({
        code: z
          .string()
          .min(1)
          .max(64)
          .regex(/^[a-z0-9_-]+$/),
      })
      .parse(input),
  )
  .handler(
    async ({
      data,
    }): Promise<{ character: CharacterRow | null; items: CharacterContent[] }> => {
      const { data: character } = await supabaseAdmin
        .from("characters")
        .select("id,code,name_ar,name_en,bio_ar,bio_en,avatar_url,color,traits")
        .eq("code", data.code)
        .eq("is_active", true)
        .maybeSingle();

      if (!character) return { character: null, items: [] };

      const { data: links } = await supabaseAdmin
        .from("content_characters")
        .select(
          "content_items!inner(id,kind,tier,age_group,title_ar,title_en,description_ar,description_en,cover_url,world_id,slug,published_at,status)",
        )
        .eq("character_id", character.id);

      type Link = { content_items: CharacterContent & { status: string } };
      const items: CharacterContent[] =
        ((links ?? []) as unknown as Link[])
          .map((l) => l.content_items)
          .filter((c) => c && c.status === "published")
          .sort((a, b) =>
            (b.published_at ?? "").localeCompare(a.published_at ?? ""),
          );

      return { character, items };
    },
  );
